import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Megaphone, Plus, RefreshCw, DollarSign, MousePointer, Eye, Target,
  TrendingUp, Sparkles, Trash2, Play, Pause, BarChart3, Users,
  Globe, Search, ArrowUpRight, ArrowDownRight, Upload, Image as ImageIcon, Video, X as XIcon
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'google', name: 'Google Ads', color: '#4285F4', icon: Globe },
  { id: 'meta', name: 'Meta Ads', color: '#1877F2', icon: Megaphone },
  { id: 'linkedin', name: 'LinkedIn Ads', color: '#0A66C2', icon: Users },
];

const OBJECTIVES = [
  { id: 'brand_awareness', label: 'Brand Awareness' },
  { id: 'traffic', label: 'Website Traffic' },
  { id: 'lead_generation', label: 'Lead Generation' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'retargeting', label: 'Retargeting' },
  { id: 'reach', label: 'Reach' },
  { id: 'engagement', label: 'Engagement' },
];

// ─── Stat Card ───
const StatCard = ({ icon: Icon, iconColor, label, value, sub, trend }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs sm:text-sm text-gray-medium truncate">{label}</span>
        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
      </div>
      <p className="text-lg sm:text-2xl font-bold text-text-primary">{value}</p>
      {sub && <p className="text-[10px] sm:text-xs text-gray-medium">{sub}</p>}
      {trend !== undefined && (
        <div className={cn("flex items-center gap-0.5 text-xs mt-1", trend >= 0 ? "text-emerald-600" : "text-red-500")}>
          {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(trend)}% vs last week
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Campaign Row ───
const CampaignRow = ({ campaign, onStatusChange, onDelete }) => {
  const { format: fc } = useCurrency();
  const plat = PLATFORMS.find(p => p.id === campaign.platform);
  const PlatIcon = plat?.icon || Globe;
  const statusStyles = {
    active: 'bg-emerald-brand text-white',
    paused: 'bg-amber-500 text-white',
    draft: 'bg-gray-400 text-white',
    completed: 'bg-blue-500 text-white',
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white border border-gray-border rounded-xl hover:shadow-sm transition-shadow" data-testid={`campaign-row-${campaign.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${plat?.color || '#666'}15` }}>
          <PlatIcon className="h-4 w-4" style={{ color: plat?.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-text-primary truncate">{campaign.name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-medium">{plat?.name}</span>
            <span className="text-xs text-gray-300">|</span>
            <span className="text-xs text-gray-medium capitalize">{campaign.objective?.replace('_', ' ')}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 text-center flex-shrink-0">
        <div>
          <p className="text-xs text-gray-medium">Spend</p>
          <p className="text-sm font-semibold text-text-primary">{fc(campaign.spend || 0)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-medium">Clicks</p>
          <p className="text-sm font-semibold text-text-primary">{(campaign.clicks || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-gray-medium">CTR</p>
          <p className="text-sm font-semibold text-text-primary">{campaign.ctr || 0}%</p>
        </div>
        <div>
          <p className="text-xs text-gray-medium">Conv.</p>
          <p className="text-sm font-semibold text-text-primary">{campaign.conversions || 0}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge className={cn("text-[10px]", statusStyles[campaign.status] || 'bg-gray-400 text-white')}>
          {campaign.status}
        </Badge>
        {campaign.status === 'active' ? (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onStatusChange(campaign.id, 'paused')} data-testid={`pause-${campaign.id}`}>
            <Pause className="h-3.5 w-3.5 text-amber-500" />
          </Button>
        ) : campaign.status === 'paused' ? (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onStatusChange(campaign.id, 'active')} data-testid={`play-${campaign.id}`}>
            <Play className="h-3.5 w-3.5 text-emerald-brand" />
          </Button>
        ) : null}
        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-medium hover:text-red-500" onClick={() => onDelete(campaign.id)} data-testid={`delete-campaign-${campaign.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// ─── Audience Card ───
const AudienceCard = ({ audience, onDelete }) => {
  const plat = PLATFORMS.find(p => p.id === audience.platform);
  const typeColors = {
    lookalike: 'bg-purple-100 text-purple-700',
    retargeting: 'bg-blue-100 text-blue-700',
    custom: 'bg-amber-100 text-amber-700',
    interest: 'bg-emerald-100 text-emerald-700',
    in_market: 'bg-pink-100 text-pink-700',
  };

  return (
    <Card className="bg-white border-gray-border" data-testid={`audience-card-${audience.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <Badge className={cn("text-[10px]", typeColors[audience.type] || 'bg-gray-100 text-gray-700')}>
            {audience.type?.replace('_', ' ')}
          </Badge>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-medium hover:text-red-500" onClick={() => onDelete(audience.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        <h4 className="font-medium text-text-primary mb-1">{audience.name}</h4>
        <p className="text-xs text-gray-medium mb-3 line-clamp-2">{audience.description}</p>
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-border">
          <div>
            <p className="text-[10px] text-gray-medium">Size</p>
            <p className="text-sm font-semibold text-text-primary">{(audience.size || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-medium">Match</p>
            <p className="text-sm font-semibold text-text-primary">{audience.match_rate || 0}%</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-medium">Platform</p>
            <p className="text-sm font-semibold capitalize" style={{ color: plat?.color }}>{plat?.name?.split(' ')[0] || audience.platform}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Platform Breakdown Bar ───
const PlatformBreakdown = ({ data }) => {
  const { format: fc } = useCurrency();
  if (!data || Object.keys(data).length === 0) return null;
  const total = Object.values(data).reduce((sum, p) => sum + (p.spend || 0), 0);

  return (
    <Card className="bg-white border-gray-border" data-testid="platform-breakdown">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading">Spend by Platform</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(data).map(([key, val]) => {
          const plat = PLATFORMS.find(p => p.id === key);
          const pct = total > 0 ? Math.round((val.spend / total) * 100) : 0;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-text-primary capitalize">{plat?.name || key}</span>
                <span className="text-xs text-gray-medium">{fc(val.spend || 0)} ({pct}%)</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: plat?.color || '#888' }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};


// ─── Main Page ───
export const PaidAdsPage = () => {
  const { format: fc, symbol: currSymbol } = useCurrency();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [newCampaign, setNewCampaign] = useState({
    platform: 'google', name: '', objective: 'traffic', budget: 500,
    daily_budget: 0, targeting: {}, ad_creative: { headline: '', description: '', cta_text: 'Learn More', media_url: '', media_type: '' }
  });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [newAudience, setNewAudience] = useState({
    name: '', platform: 'meta', type: 'custom', size: 0, match_rate: 0, description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, campaignsRes, audiencesRes] = await Promise.all([
        axios.get(`${API_URL}/api/ads/overview`),
        axios.get(`${API_URL}/api/ads/campaigns`),
        axios.get(`${API_URL}/api/ads/audiences`),
      ]);
      setOverview(overviewRes.data);
      setCampaigns(campaignsRes.data.campaigns || []);
      setAudiences(audiencesRes.data.audiences || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) { toast.error('Campaign name is required'); return; }
    try {
      await axios.post(`${API_URL}/api/ads/campaigns`, newCampaign);
      toast.success('Campaign created!');
      setCreateOpen(false);
      setNewCampaign({ platform: 'google', name: '', objective: 'traffic', budget: 500, daily_budget: 0, targeting: {}, ad_creative: { headline: '', description: '', cta_text: 'Learn More', media_url: '', media_type: '' } });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  // Upload creative image or video for the ad. Uses /api/media (base64 upload)
  // so the file is persisted server-side and returns a stable file_url.
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Max 25MB for image, 100MB for video (aligned with /api/media limits)
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 100_000_000 : 10_000_000;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '100' : '10'}MB.`);
      return;
    }
    setUploadingMedia(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await axios.post(`${API_URL}/api/media`, {
            title: `Campaign creative — ${newCampaign.name || 'untitled'}`,
            media_type: isVideo ? 'video' : 'image',
            description: 'Ad creative uploaded from Paid Ads campaign builder',
            assignment: 'campaign',
            data_base64: reader.result,
            file_name: file.name,
            mime_type: file.type,
          });
          const url = data.file_url?.startsWith('/api/')
            ? `${API_URL}${data.file_url}`
            : data.file_url;
          setNewCampaign(p => ({
            ...p,
            ad_creative: { ...p.ad_creative, media_url: url, media_type: isVideo ? 'video' : 'image' },
          }));
          toast.success('Media uploaded');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
          setUploadingMedia(false);
        }
      };
      reader.onerror = () => { toast.error('Could not read file'); setUploadingMedia(false); };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploadingMedia(false);
      toast.error('Upload failed');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/ads/campaigns/${id}`, { status });
      toast.success(`Campaign ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/ads/campaigns/${id}`);
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleCreateAudience = async () => {
    if (!newAudience.name.trim()) { toast.error('Audience name is required'); return; }
    try {
      await axios.post(`${API_URL}/api/ads/audiences`, newAudience);
      toast.success('Audience created!');
      setAudienceOpen(false);
      setNewAudience({ name: '', platform: 'meta', type: 'custom', size: 0, match_rate: 0, description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create audience');
    }
  };

  const handleDeleteAudience = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/ads/audiences/${id}`);
      toast.success('Audience deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete audience');
    }
  };

  // Filtering
  const filtered = campaigns.filter(c => {
    if (filterPlatform !== 'all' && c.platform !== filterPlatform) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  const spendPct = overview?.total_budget ? Math.round((overview.total_spend / overview.total_budget) * 100) : 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="paid-ads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Paid Ads<PageInfoTooltip pageKey="paid-ads" /></h1>
          <div className="flex items-center gap-2">
            <p className="text-gray-medium">Manage your advertising campaigns across platforms</p>
            {overview && (
              <Badge
                className={cn("text-[10px]",
                  overview.data_source === 'live'
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                )}
                data-testid="ads-data-source"
              >
                {overview.data_source === 'live' ? 'Live Data' : 'Demo Data'}
              </Badge>
            )}
          </div>
        </div>
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setCreateOpen(true)} data-testid="create-campaign-btn">
          <Plus className="h-4 w-4" /> New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-white border-gray-border col-span-2" data-testid="stat-budget">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs sm:text-sm text-gray-medium">Total Spend / Budget</span>
              <DollarSign className="h-4 w-4 text-emerald-brand" />
            </div>
            <div className="flex items-baseline gap-2 mb-2 flex-wrap">
              <span className="text-lg sm:text-2xl font-bold text-text-primary">${(overview?.total_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-xs sm:text-sm text-gray-medium">/ ${(overview?.total_budget || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <Progress value={spendPct} className="h-2" />
            <p className="text-[10px] text-gray-medium mt-1">{spendPct}% utilized</p>
          </CardContent>
        </Card>
        <StatCard icon={TrendingUp} iconColor="#10B981" label="ROAS" value={`${overview?.roas || 0}x`} trend={12} />
        <StatCard icon={Eye} iconColor="#3B82F6" label="Impressions" value={(overview?.total_impressions || 0).toLocaleString()} />
        <StatCard icon={MousePointer} iconColor="#8B5CF6" label="Clicks" value={(overview?.total_clicks || 0).toLocaleString()} sub={`CTR: ${overview?.avg_ctr || 0}%`} />
        <StatCard icon={Target} iconColor="#F59E0B" label="Conversions" value={overview?.total_conversions || 0} sub={`CPC: ${currSymbol}${overview?.avg_cpc || 0}`} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns" className="text-xs sm:text-sm whitespace-nowrap">
            Campaigns ({campaigns.length})
          </TabsTrigger>
          <TabsTrigger value="audiences" data-testid="tab-audiences" className="text-xs sm:text-sm whitespace-nowrap">
            Audiences ({audiences.length})
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights" className="text-xs sm:text-sm whitespace-nowrap">
            Insights
          </TabsTrigger>
        </TabsList>

        {/* ─── Campaigns Tab ─── */}
        <TabsContent value="campaigns" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-medium" />
              <Input
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="campaign-search"
              />
            </div>
            <Select value={filterPlatform} onValueChange={setFilterPlatform}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-platform">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length > 0 ? (
            <div className="space-y-3">
              {filtered.map(c => (
                <CampaignRow key={c.id} campaign={c} onStatusChange={handleStatusChange} onDelete={handleDeleteCampaign} />
              ))}
            </div>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Megaphone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">{searchQuery || filterPlatform !== 'all' || filterStatus !== 'all' ? 'No campaigns match your filters' : 'No campaigns yet'}</p>
                <Button className="bg-emerald-brand hover:bg-emerald-hover mt-4" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Create Campaign
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Platform Breakdown */}
          {overview?.by_platform && <PlatformBreakdown data={overview.by_platform} />}
        </TabsContent>

        {/* ─── Audiences Tab ─── */}
        <TabsContent value="audiences" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setAudienceOpen(true)} data-testid="create-audience-btn">
              <Plus className="h-4 w-4" /> New Audience
            </Button>
          </div>
          {audiences.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {audiences.map(a => <AudienceCard key={a.id} audience={a} onDelete={handleDeleteAudience} />)}
            </div>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">No audiences created yet</p>
                <Button className="bg-emerald-brand hover:bg-emerald-hover mt-4" onClick={() => setAudienceOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Create Audience
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Insights Tab ─── */}
        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Weekly Trend */}
            <Card className="bg-white border-gray-border" data-testid="weekly-trend">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-emerald-brand" /> Weekly Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(overview?.weekly_trend || []).map((w, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-medium w-14 flex-shrink-0">{w.week}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                        <div
                          className="h-full bg-emerald-brand/70 rounded-full"
                          style={{ width: `${Math.min((w.spend / 800) * 100, 100)}%` }}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-text-primary">
                          {currSymbol}{w.spend} | {w.conversions} conv
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Campaigns */}
            <Card className="bg-white border-gray-border" data-testid="top-campaigns">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" /> Top Performing Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {campaigns
                    .filter(c => c.status === 'active')
                    .sort((a, b) => (b.roas || 0) - (a.roas || 0))
                    .slice(0, 5)
                    .map((c, i) => {
                      const plat = PLATFORMS.find(p => p.id === c.platform);
                      return (
                        <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-off-white">
                          <span className="text-sm font-bold text-gray-medium w-5">{i + 1}</span>
                          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${plat?.color || '#888'}15` }}>
                            {plat?.icon && <plat.icon className="h-3.5 w-3.5" style={{ color: plat.color }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">{c.name}</p>
                          </div>
                          <Badge className="bg-emerald-brand/10 text-emerald-brand text-xs">{c.roas}x ROAS</Badge>
                        </div>
                      );
                    })}
                  {campaigns.filter(c => c.status === 'active').length === 0 && (
                    <p className="text-sm text-gray-medium text-center py-4">No active campaigns</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Recommendations */}
          <Card className="bg-white border-gray-border" data-testid="ai-recommendations">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" /> AI Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
                  <p className="text-xs font-medium text-purple-700 mb-1">Budget Optimization</p>
                  <p className="text-xs text-purple-600">Shift 15% budget from Display to Search campaigns for better ROAS</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-xs font-medium text-blue-700 mb-1">Audience Expansion</p>
                  <p className="text-xs text-blue-600">Your lookalike audiences perform 2.3x better. Create more variants</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <p className="text-xs font-medium text-emerald-700 mb-1">Creative Refresh</p>
                  <p className="text-xs text-emerald-600">Ad fatigue detected on Meta campaigns. Refresh creatives for +18% CTR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Create Campaign Dialog ─── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create New Campaign</DialogTitle>
            <DialogDescription>Configure targeting, budget, and creative for your new ad campaign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Platform</Label>
              <Select value={newCampaign.platform} onValueChange={v => setNewCampaign(p => ({ ...p, platform: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-campaign-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g. Summer Sale 2026"
                value={newCampaign.name}
                onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))}
                className="mt-1"
                data-testid="new-campaign-name"
              />
            </div>
            <div>
              <Label>Objective</Label>
              <Select value={newCampaign.objective} onValueChange={v => setNewCampaign(p => ({ ...p, objective: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-campaign-objective"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OBJECTIVES.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Total Budget ({currSymbol})</Label>
                <Input
                  type="number"
                  value={newCampaign.budget}
                  onChange={e => setNewCampaign(p => ({ ...p, budget: parseFloat(e.target.value) || 0 }))}
                  className="mt-1"
                  data-testid="new-campaign-budget"
                />
              </div>
              <div>
                <Label>Daily Budget ({currSymbol})</Label>
                <Input
                  type="number"
                  value={newCampaign.daily_budget || Math.round(newCampaign.budget / 30)}
                  onChange={e => setNewCampaign(p => ({ ...p, daily_budget: parseFloat(e.target.value) || 0 }))}
                  className="mt-1"
                  data-testid="new-campaign-daily-budget"
                />
              </div>
            </div>
            <div>
              <Label>Ad Headline</Label>
              <Input
                placeholder="Your attention-grabbing headline"
                value={newCampaign.ad_creative.headline}
                onChange={e => setNewCampaign(p => ({ ...p, ad_creative: { ...p.ad_creative, headline: e.target.value } }))}
                className="mt-1"
                data-testid="new-campaign-headline"
              />
            </div>
            <div>
              <Label>Ad Description</Label>
              <Textarea
                placeholder="Describe your offer..."
                value={newCampaign.ad_creative.description}
                onChange={e => setNewCampaign(p => ({ ...p, ad_creative: { ...p.ad_creative, description: e.target.value } }))}
                className="mt-1"
                rows={3}
                data-testid="new-campaign-description"
              />
            </div>

            {/* Media upload — image or video creative */}
            <div>
              <Label className="flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5 text-emerald-600" /> Ad Creative (Image or Video)
              </Label>
              <p className="text-[10px] text-gray-medium mt-0.5">Max 10MB for images, 100MB for videos. JPG, PNG, WebP, MP4, WebM supported.</p>
              {newCampaign.ad_creative.media_url ? (
                <div className="mt-2 relative border border-gray-border rounded-lg overflow-hidden bg-gray-50" data-testid="campaign-media-preview">
                  {newCampaign.ad_creative.media_type === 'video' ? (
                    <video src={newCampaign.ad_creative.media_url} controls className="w-full max-h-48 object-contain bg-black" />
                  ) : (
                    <img src={newCampaign.ad_creative.media_url} alt="Ad creative" className="w-full max-h-48 object-contain" />
                  )}
                  <button
                    type="button"
                    onClick={() => setNewCampaign(p => ({ ...p, ad_creative: { ...p.ad_creative, media_url: '', media_type: '' } }))}
                    className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white rounded-full p-1 shadow"
                    data-testid="remove-campaign-media-btn"
                    aria-label="Remove media"
                  >
                    <XIcon className="h-3.5 w-3.5 text-red-500" />
                  </button>
                  <div className="text-[10px] text-gray-medium px-2 py-1 flex items-center gap-1">
                    {newCampaign.ad_creative.media_type === 'video' ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                    {newCampaign.ad_creative.media_type}
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="campaign-media-file"
                  className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-gray-border rounded-lg py-6 cursor-pointer hover:border-emerald-brand hover:bg-emerald-50/30 transition-colors"
                  data-testid="campaign-media-upload-label"
                >
                  {uploadingMedia ? (
                    <><RefreshCw className="h-4 w-4 animate-spin text-emerald-600" /> <span className="text-sm text-gray-medium">Uploading…</span></>
                  ) : (
                    <><Upload className="h-4 w-4 text-emerald-600" /> <span className="text-sm text-gray-medium">Click to upload image or video</span></>
                  )}
                  <input
                    id="campaign-media-file"
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    disabled={uploadingMedia}
                    onChange={handleMediaUpload}
                    data-testid="campaign-media-input"
                  />
                </label>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreateCampaign} data-testid="submit-campaign-btn">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Create Audience Dialog ─── */}
      <Dialog open={audienceOpen} onOpenChange={setAudienceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Audience</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Audience Name</Label>
              <Input
                placeholder="e.g. High-Value Shoppers"
                value={newAudience.name}
                onChange={e => setNewAudience(p => ({ ...p, name: e.target.value }))}
                className="mt-1"
                data-testid="new-audience-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Platform</Label>
                <Select value={newAudience.platform} onValueChange={v => setNewAudience(p => ({ ...p, platform: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={newAudience.type} onValueChange={v => setNewAudience(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="lookalike">Lookalike</SelectItem>
                    <SelectItem value="retargeting">Retargeting</SelectItem>
                    <SelectItem value="interest">Interest</SelectItem>
                    <SelectItem value="in_market">In-Market</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Audience Size</Label>
                <Input type="number" value={newAudience.size} onChange={e => setNewAudience(p => ({ ...p, size: parseInt(e.target.value) || 0 }))} className="mt-1" />
              </div>
              <div>
                <Label>Match Rate (%)</Label>
                <Input type="number" value={newAudience.match_rate} onChange={e => setNewAudience(p => ({ ...p, match_rate: parseInt(e.target.value) || 0 }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this audience segment..."
                value={newAudience.description}
                onChange={e => setNewAudience(p => ({ ...p, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAudienceOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreateAudience} data-testid="submit-audience-btn">
              Create Audience
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaidAdsPage;
